/**
 * The gates: what a finding must survive before a person ever sees it.
 *
 * ⛔ NO MODEL IS ASKED HOW SURE IT IS, ANYWHERE IN THIS FILE. Every component below is a boolean
 * over an artifact we already hold — the checked-out tree, the pull request's own hunks, the
 * refuter's citation. A person who distrusts a verdict can recompute it and point at the component
 * they disagree with. Measured (src/types/finding.ts): model self-scoring reaches AUROC 0.60-0.63
 * against a TF-IDF baseline of 0.83-0.95, and over 78% of self-reported scores land on three round
 * numbers, so a threshold over them has almost no resolution.
 *
 * ⛔ TWO GATES DROP; THE THIRD CLASSIFIES. docs/ARCHITECTURE.md says all three of `citedLineExists`,
 * `quoteIsByteIdentical` and `introducedHere` are hard gates that keep a finding out of the queue —
 * and, four paragraphs earlier, that a `pre-existing` finding is "never posted — shown in the triage
 * queue only", with `surface_in_ui = true` and an auto-dismiss rule in config.md that could not fire
 * against something already deleted. Both cannot be true. The reconciliation, and it is the reading
 * every other document supports:
 *   · `citedLineExists` and `quoteIsByteIdentical` DROP. A finding that cannot cite a real line or
 *     quotes it wrongly is not a finding at all, and there is nothing for a person to adjudicate.
 *   · `introducedHere` CLASSIFIES. The line is real and the quote is honest; the author simply did
 *     not write it. That is a true finding about somebody else's code — never commented on, never
 *     blocking, still visible in triage, and promotable by a human.
 * `passesHardGates()` keeps its meaning exactly: it is the POSTABLE test, and a pre-existing finding
 * correctly fails it.
 */

import { isAnchorable, wasIntroducedHere, type AnchorSet } from "../github/pr.js"
import { fingerprintOf as storeFingerprintOf } from "../triage/store.js"
import { CloneError, readFileAt, resolveInClone } from "../repo/clone.js"
import { readFileSync } from "node:fs"
import {
  componentScore,
  passesHardGates,
  type Components,
  type Finding,
  type FindingClass,
  type Severity,
} from "../types/finding.js"
import {
  citedLines,
  hasFailureScenario,
  renderFailureScenario,
  type CandidateFinding,
  type ReviewClone,
} from "./candidate.js"
import { NOT_REFUTED, type RefutationResult } from "./refute.js"

/**
 * ⛔ The two components that DROP a finding. Deliberately a subset of `HARD_GATES` — see the file
 * header for why the third one classifies instead.
 */
export const DROP_GATES = ["citedLineExists", "quoteIsByteIdentical"] as const satisfies readonly (keyof Components)[]

export type DropGateName = (typeof DROP_GATES)[number]

/** The first dropping gate this finding fails, or `undefined` when it fails none of them. */
export function failedDropGate(c: Components): DropGateName | undefined {
  return DROP_GATES.find((k) => !c[k])
}

/** Something that happened while gating which is not a property of the finding. Named, never swallowed. */
export interface GateAnomaly {
  readonly kind: "path-escape" | "unreadable-rule-source"
  /** The finding this happened to, by title, so a log line is actionable. */
  readonly finding: string
  readonly detail: string
}

export interface ComputeOptions {
  /** Defaults to `not_run` — the honest state for a finding nothing has attacked. */
  readonly refutation?: RefutationResult
  /**
   * ⛔ Called for a path escape rather than folding it into "that finding did not check out".
   * src/repo/clone.ts is explicit that a path escape is an attack or a bug and must never be
   * reported as an ordinary miss. It is still not allowed to abort the run: a model emitting
   * `../../etc/passwd` must cost one finding, not the review — the same asymmetry config.md applies
   * to a malformed SIBYL.md, because anything else hands an author a one-line denial of service
   * against their own reviewer.
   */
  readonly onAnomaly?: (a: GateAnomaly) => void
}

/**
 * Fill all six components from real artifacts.
 *
 * `clone` is the tree at the reviewed sha; `anchorSet` is built from the pull request's own patches.
 * Nothing here consults the model that produced the finding.
 */
export function computeComponents(
  finding: CandidateFinding,
  clone: ReviewClone,
  anchorSet: AnchorSet,
  opts: ComputeOptions = {},
): Components {
  const refutation = opts.refutation ?? NOT_REFUTED
  const lines = citedLines(finding.anchor)

  // ── 1 · Does the cited line exist? ──
  let actual: string[] | undefined
  if (lines.length === 0) {
    actual = undefined
  } else {
    const read: string[] = []
    for (const n of lines) {
      let text: string | undefined
      try {
        text = readFileAt(clone.path, finding.anchor.path, n)
      } catch (err) {
        opts.onAnomaly?.({
          kind: "path-escape",
          finding: finding.title,
          detail: `${finding.anchor.path}:${n} — ${err instanceof CloneError ? err.code : "unreadable"}: ${
            err instanceof Error ? err.message : String(err)
          }`,
        })
        text = undefined
      }
      if (text === undefined) {
        read.length = 0
        break
      }
      read.push(text)
    }
    actual = read.length === lines.length ? read : undefined
  }
  const citedLineExists = actual !== undefined

  // ── 2 · Is the quote honest? ──
  //
  // ⛔ BYTE FOR BYTE, and no normalisation of any kind. Not trimmed, not re-indented, not
  // case-folded, not whitespace-collapsed. Every softening of this comparison is a way for a
  // paraphrase to pass as a quote, and a paraphrased quote is how a finding about code that does not
  // exist reaches a person looking authoritative. Leading indentation is content; so is a trailing
  // space, which is the entire subject of a real class of finding. The one terminator subtlety — a
  // CRLF's CR belongs to the terminator and not the line — is handled once, in readFileAt.
  const quoteIsByteIdentical = actual !== undefined && actual.join("\n") === finding.quote

  // ── 3 · Did this pull request introduce it? ──
  //
  // ⛔ Arithmetic over the hunks, never a judgement, so it cannot be argued with. For a range, any
  // introduced line in it makes the quoted code this author's: the shape they wrote is part of what
  // the finding is about.
  const introducedHere = lines.some((line) =>
    wasIntroducedHere(anchorSet, { path: finding.anchor.path, line, side: finding.anchor.side }),
  )

  return {
    citedLineExists,
    quoteIsByteIdentical,
    introducedHere,
    // ⛔ Only "stands" earns this. "not_run" is not a survival — nothing attacked it.
    survivedRefutation: refutation.verdict === "stands",
    hasFailureScenario: hasFailureScenario(finding.failureScenario),
    ruleCitationChecksOut: checkRuleCitation(finding, clone, opts),
  }
}

/**
 * Is the quoted rule really in the file the finding names?
 *
 * ⚠️ A finding with no rule citation scores `false` here, and that is not an accusation — it is the
 * absence of a bonus. This component only ranks; it is not a gate.
 */
function checkRuleCitation(finding: CandidateFinding, clone: ReviewClone, opts: ComputeOptions): boolean {
  const cite = finding.ruleCitation
  if (cite === undefined) return false
  const needle = cite.text.trim()
  if (needle === "") return false
  let text: string
  try {
    text = readFileSync(resolveInClone(clone.path, cite.source), "utf8")
  } catch (err) {
    if (err instanceof CloneError) {
      opts.onAnomaly?.({
        kind: "path-escape",
        finding: finding.title,
        detail: `rule source ${cite.source} — ${err.code}: ${err.message}`,
      })
    } else {
      opts.onAnomaly?.({
        kind: "unreadable-rule-source",
        finding: finding.title,
        detail: `${cite.source}: ${err instanceof Error ? err.message : String(err)}`,
      })
    }
    return false
  }
  // ⚠️ Line endings are normalised and nothing else is. A rule quoted from a file checked out with
  // CRLF is the same rule; a rule with a word changed is not.
  return text.replaceAll("\r\n", "\n").includes(needle.replaceAll("\r\n", "\n"))
}

/**
 * The class of a finding, computed.
 *
 * ⭐ `shadow` wins over everything: a dimension that was run while disabled produces findings that
 * are visible and never postable, whatever else is true of them.
 */
export function classOf(candidateIsShadow: boolean, components: Components): FindingClass {
  if (candidateIsShadow) return "shadow"
  return components.introducedHere ? "introduced" : "pre_existing"
}

/**
 * Stable across a rebase.
 *
 * ⛔ NEVER KEYED ON (path, line). Line numbers move under every rebase and a force-push moves all of
 * them, so a fingerprint built from them makes every dismissal evaporate the moment the author
 * pushes — which is the single failure that empties a triage queue of its value. Keyed on the
 * dimension plus a hash of the surrounding code, normalised for indentation and blank lines so a
 * reformat does not count as a different defect.
 *
 * ⚠️ `dimensionId` stands in for the "normalised rule id" the design calls for, because there is no
 * rule taxonomy yet. When W7 gives findings rule ids, this is where they go — and every stored
 * fingerprint changes, which is a migration and not a tweak.
 */
export function fingerprintOf(finding: CandidateFinding, clone: ReviewClone): string {
  // ⛔ ONE IMPLEMENTATION, IN THE TRIAGE STORE. This module and the store each had their own, and
  // they disagreed on the prefix and on how whitespace inside a line is normalised — so a dismissal
  // written by one would never match a finding produced by the other, which is exactly the failure
  // the fingerprint exists to prevent. Two hashes of the same thing is not a duplication smell, it
  // is a silent correctness bug, and it was found by a lane reading the other lane's file.
  const lines = citedLines(finding.anchor)
  const first = lines[0] ?? finding.anchor.line
  const last = lines[lines.length - 1] ?? finding.anchor.line
  const window: string[] = []
  for (let n = Math.max(1, first - CONTEXT_LINES); n <= last + CONTEXT_LINES; n++) {
    let text: string | undefined
    try {
      text = readFileAt(clone.path, finding.anchor.path, n)
    } catch {
      text = undefined
    }
    if (text !== undefined) window.push(text)
  }
  // ⚠️ Falls back to the quote when the file cannot be read, so a finding that failed the citation
  // gate still gets a stable identity — a fingerprint that is sometimes absent is a key that is
  // sometimes null.
  const contextLines = window.length > 0 ? window : [finding.quote]
  return storeFingerprintOf({ dimensionId: finding.dimensionId, contextLines })
}

/** Enough of the function to identify it, small enough that an edit two screens away does not move it. */
const CONTEXT_LINES = 3

/**
 * Assemble the finding a person will see, from the claim and the checks.
 *
 * ⛔ The only constructor of a `Finding` in this codebase. `components` and `cls` are arguments that
 * must be computed, never fields a caller can invent.
 */
export function toFinding(candidate: CandidateFinding, components: Components, clone: ReviewClone): Finding {
  return {
    fingerprint: fingerprintOf(candidate, clone),
    dimensionId: candidate.dimensionId,
    severity: candidate.severity,
    cls: classOf(candidate.shadow === true, components),
    title: candidate.title,
    anchor: candidate.anchor,
    quote: candidate.quote,
    consequence: candidate.consequence,
    failureScenario: renderFailureScenario(candidate.failureScenario),
    recommendedFix: candidate.recommendedFix,
    effort: candidate.effort,
    ...(candidate.suggestion === undefined ? {} : { suggestion: candidate.suggestion }),
    reasoning: candidate.reasoning,
    components,
    provenance: candidate.provenance,
  }
}

/**
 * A finding and what the refuter made of it.
 *
 * ⛔ The verdict travels beside the finding rather than collapsing into `survivedRefutation`, because
 * the boolean cannot tell "disproved, here is the line" from "the refuter never ran" — and one of
 * those must drop the finding while the other must not. A single boolean here would mean a backend
 * outage silently deleted every finding in the review.
 */
export interface ReviewedFinding {
  readonly finding: Finding
  readonly refutation: RefutationResult
}

export type DropGate = "cited_line_exists" | "quote_is_byte_identical" | "refuted" | "duplicate"

export interface DroppedFinding {
  readonly finding: Finding
  readonly gate: DropGate
  /** Plain language, and specific enough to be argued with. Never "failed a check". */
  readonly reason: string
}

export interface GateReport {
  /** Survivors, classified and ranked. This is the triage queue. */
  readonly kept: readonly Finding[]
  /** ⛔ Everything removed, with the reason. A drop is never silent. */
  readonly dropped: readonly DroppedFinding[]
}

/**
 * Drop what cannot stand, classify what the author did not write, collapse duplicates, and order
 * what is left.
 *
 * ⛔ THE ONLY FOUR REASONS A FINDING IS REMOVED HERE, and each one is evidence rather than an
 * opinion: its line does not exist, its quote is not the line, the refuter disproved it with a
 * citation, or another finding already says it about the same line. Nothing is dropped for being
 * unconvincing, low severity, or pre-existing.
 */
export function applyGates(findings: readonly ReviewedFinding[]): GateReport {
  const dropped: DroppedFinding[] = []
  const survivors: Finding[] = []

  for (const { finding, refutation } of findings) {
    const c = finding.components
    const failed = failedDropGate(c)
    if (failed !== undefined) {
      const where = `${finding.anchor.path}:${finding.anchor.line}`
      dropped.push({
        finding,
        gate: failed === "citedLineExists" ? "cited_line_exists" : "quote_is_byte_identical",
        reason:
          failed === "citedLineExists"
            ? `${where} does not exist in the reviewed tree`
            : `the quoted code is not what ${where} says, byte for byte`,
      })
      continue
    }
    // ⛔ Only a refutation carrying verified evidence drops anything. `not_run` and `stands` both
    // survive — dropping requires evidence; doubt does not.
    if (refutation.verdict === "refuted") {
      const d = refutation.disproof
      dropped.push({
        finding,
        gate: "refuted",
        reason:
          d === undefined
            ? `disproved: ${refutation.why}`
            : `disproved by ${d.path}:${d.line} — ${refutation.why}`,
      })
      continue
    }
    // ⭐ Reclassified here rather than trusted, so the class is a property of the artifacts even if
    // the finding was assembled somewhere careless.
    const cls = classOf(finding.cls === "shadow", c)
    survivors.push(cls === finding.cls ? finding : { ...finding, cls })
  }

  // ── Dedupe, at line scope (config.md [triage] dedupe_scope = "line") ──
  //
  // ⭐ The same defect found by three dimensions is one defect. `comments.max_per_line = 1` says the
  // rest out loud: two comments on one line is a dedupe bug, not a thorough review.
  const ranked = [...survivors].sort(compareForQueue)
  const seen = new Map<string, Finding>()
  const kept: Finding[] = []
  for (const f of ranked) {
    const key = `${f.anchor.path}|${f.anchor.side}|${f.anchor.line}`
    const winner = seen.get(key)
    if (winner === undefined) {
      seen.set(key, f)
      kept.push(f)
      continue
    }
    dropped.push({
      finding: f,
      gate: "duplicate",
      reason: `the same line is already covered by "${winner.title}" (${winner.dimensionId}), which ranked higher`,
    })
  }

  return { kept, dropped }
}

const SEVERITY_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 }
const CLASS_RANK: Record<FindingClass, number> = { introduced: 0, pre_existing: 1, shadow: 2 }

/**
 * Queue order: severity, then class, then how many components held, then path and line.
 *
 * ⚠️ `[triage].order` also lists `dimension_weight`, which needs the resolved config and therefore
 * belongs to the caller that has one. It is not silently ignored — it is simply not a tie-break this
 * function can make, and the ordering below is total without it, so adding it later cannot reorder
 * anything that was already decided.
 *
 * ⛔ The component count orders the queue and never reaches the pull-request author. A displayed
 * number reads as a measurement; this one is a count of checks that held.
 */
export function compareForQueue(a: Finding, b: Finding): number {
  return (
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
    CLASS_RANK[a.cls] - CLASS_RANK[b.cls] ||
    componentScore(b.components) - componentScore(a.components) ||
    a.anchor.path.localeCompare(b.anchor.path) ||
    a.anchor.line - b.anchor.line ||
    a.dimensionId.localeCompare(b.dimensionId)
  )
}

/**
 * May this finding be posted to GitHub?
 *
 * ⛔ All three hard gates, which is what makes `pre_existing` unpostable without deleting it: a pull
 * request author is not accountable for code they did not touch, and commenting on it is how a
 * review becomes something people route around. Anchorability is checked too, because one comment
 * outside the diff fails the entire review call with a 422 and nothing lands.
 */
export function isPostable(finding: Finding, anchorSet: AnchorSet): boolean {
  if (finding.cls === "shadow") return false
  return passesHardGates(finding.components) && isAnchorable(anchorSet, finding.anchor)
}

/**
 * The components rendered as passed/failed checks.
 *
 * ⛔ Never a number, and never shown to the pull-request author. A user abandoned a competing tool
 * after three pull requests specifically because it attached confidence 4/5 to findings that were
 * wrong — *"worse than no score at all."*
 */
export function describeComponents(c: Components): string[] {
  return [
    `${c.citedLineExists ? "✓" : "✗"} the cited line exists`,
    `${c.quoteIsByteIdentical ? "✓" : "✗"} the quote is the line, byte for byte`,
    `${c.introducedHere ? "✓" : "✗"} this pull request introduced it`,
    `${c.survivedRefutation ? "✓" : "✗"} it survived an attempt to disprove it`,
    `${c.hasFailureScenario ? "✓" : "✗"} it states concrete inputs and a wrong outcome`,
    `${c.ruleCitationChecksOut ? "✓" : "✗"} the rule it cites is in the file it names`,
  ]
}
